import type { Maquina, RetornoLinha } from '@pcp/contracts';
import { useUpdateMaquinas, useMaquinas } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { FORM_SUMMARY } from './shared';

const c = t.cadastros.machines;

/** Cadastro fixo: só edita (lote parcial), nunca cria nem exclui. */
export function MaquinasTab({ canEdit }: { canEdit: boolean }) {
  const query = useMaquinas();
  const update = useUpdateMaquinas();

  const columns: Column<Maquina>[] = [
    { key: 'maquina', header: c.machine, read: (r) => <strong>{r.maquina}</strong> },
    {
      key: 'descricao',
      header: c.description,
      read: (r) => r.descricao,
      edit: { kind: 'text', field: 'descricao', width: 140 },
    },
    { key: 'grupo', header: c.group, read: (r) => r.grupo },
    { key: 'nr-linha', header: c.line, numeric: true, read: (r) => r['nr-linha'] },
    {
      key: 'qtd-turnos',
      header: c.shifts,
      numeric: true,
      read: (r) => r['qtd-turnos'],
      edit: { kind: 'number', field: 'qtd-turnos', step: 1, min: 0, max: 4 },
    },
    {
      key: 'pct-oee-planej',
      header: c.oee,
      numeric: true,
      read: (r) => r['pct-oee-planej'],
      edit: { kind: 'number', field: 'pct-oee-planej', step: 1, min: 0, max: 100 },
    },
    {
      key: 'largura-min',
      header: c.minWidth,
      numeric: true,
      read: (r) => r['largura-min'] ?? '–',
      edit: { kind: 'number', field: 'largura-min', step: 1, min: 0 },
    },
    {
      key: 'largura-max',
      header: c.maxWidth,
      numeric: true,
      read: (r) => r['largura-max'] ?? '–',
      edit: { kind: 'number', field: 'largura-max', step: 1, min: 0 },
    },
    {
      key: 'ativo',
      header: c.active,
      read: (r) => (r.ativo ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'ativo' },
    },
  ];

  const save = async (row: Maquina, changes: Partial<Maquina>): Promise<RetornoLinha> => {
    const [r] = await update.mutateAsync([{ ...changes, maquina: row.maquina, version: row.version }]);
    return r!;
  };

  return (
    <section className="panel" aria-labelledby="tab-machines">
      <h2 id="tab-machines">{t.cadastros.tabs.machines}</h2>
      <p className="hint">{c.hint}</p>
      {query.isLoading ? <LoadingBlock /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={query.data}
          rowKey={(r) => r.maquina}
          rowLabel={(r) => r.descricao}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onReload={() => void query.refetch()}
        />
      ) : null}
      {FORM_SUMMARY}
    </section>
  );
}
